import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

interface Transaction {
    id: string;
    transaction_type: string;
    amount: string | number;
    status: string;
    description: string;
    transaction_date: string;
}

interface Loan {
    loan_application_number: string;
    current_outstanding: string | number;
    status: string;
    loan_amount: string | number;
    tenure_months: number;
    tenure_unit?: string;
    total_overdue?: string | number;
    transactions?: Transaction[];
}

interface LoanCardProps {
    loan: Loan;
    onPayClick: (loan: Loan) => void;
}

const LoanCard: React.FC<LoanCardProps> = ({ loan, onPayClick }) => {
    const navigate = useNavigate();

    const getStatusColor = (status: string, totalOverdue: number) => {
        if (totalOverdue > 0) return 'bg-rose-500 hover:bg-rose-600';
        switch (status) {
            case 'ACTIVE':
            case 'DISBURSED': return 'bg-green-500 hover:bg-green-600';
            case 'CLOSED': return 'bg-gray-500 hover:bg-gray-600';
            case 'FORECLOSED': return 'bg-gray-700 hover:bg-gray-800';
            case 'DEFAULTED': return 'bg-red-500 hover:bg-red-600';
            case 'NPA': return 'bg-red-700 hover:bg-red-800';
            default: return 'bg-blue-500 hover:bg-blue-600';
        }
    };

    const totalOverdueVal = parseFloat(String(loan.total_overdue || 0));

    return (
        <Card
            className={`w-full shadow-md mb-4 border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow bg-white ${totalOverdueVal > 0 ? 'overdue-card-container ring-1 ring-rose-200' : ''}`}
            onClick={() => navigate(`/lms/loan/${loan.loan_application_number}`)}
        >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                    Loan #{loan.loan_application_number}
                </CardTitle>
                <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(loan.status, totalOverdueVal)}>
                        {totalOverdueVal > 0 ? 'Overdue' : (['ACTIVE', 'DISBURSED'].includes(loan.status) ? 'On Time' : loan.status)}
                    </Badge>
                </div>
            </CardHeader>

            <CardContent>
                <div className="text-2xl font-bold">₹{parseFloat(String(loan.current_outstanding)).toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                    Outstanding Balance
                </p>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-gray-500">Original Amount</p>
                        <p className="font-semibold">₹{parseFloat(String(loan.loan_amount)).toFixed(2)}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">Tenure</p>
                        <p className="font-semibold">{loan.tenure_months} {loan.tenure_unit || 'Months'}</p>
                    </div>
                </div>
            </CardContent>

            <CardFooter>
                <Button
                    variant="outline"
                    className="w-full mr-2"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/lms/loan/${loan.loan_application_number}`);
                    }}
                >
                    View Details
                </Button>
                {['ACTIVE', 'DISBURSED'].includes(loan.status) && (
                    <Button
                        className={`w-full font-bold text-white ${totalOverdueVal > 0 ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPayClick(loan);
                        }}
                    >
                        {totalOverdueVal > 0 ? 'Pay Overdue' : 'Make Repayment'}
                    </Button>
                )}
            </CardFooter>
        </Card>
    );
};

export default LoanCard;
